import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";

const industries = [
  {
    icon: "ri-home-gear-line",
    title: "Home Services",
    subtitle: "Plumbing, roofing, solar, landscaping, contractors",
    painPoints: "Missed calls, delayed callbacks, lost jobs",
    solution: "Instant call handling, automated booking, follow-up sequences",
    outcome: "More jobs booked without adding staff",
    img: "https://public.readdy.ai/ai/img_res/c3a70e39889536816224e5b27da3f0c2.jpg",
  },
  {
    icon: "ri-scales-line",
    title: "Legal Services",
    subtitle: "Law firms, attorneys, intake teams",
    painPoints: "Unqualified leads, slow intake, missed consultations",
    solution: "Automated intake, qualification flows, scheduling",
    outcome: "Better clients, less admin time",
    img: "https://readdy.ai/api/search-image?query=modern%20law%20office%20interior%20clean%20minimal%20professional%20desk%20books%20neutral%20tones%20elegant%20sophisticated%20premium%20photography&width=800&height=500&seq=ind-legal-002&orientation=landscape",
  },
  {
    icon: "ri-heart-pulse-line",
    title: "Med Spa & Wellness",
    subtitle: "Aesthetic clinics, med spas, wellness centers",
    painPoints: "Leads not converting, inconsistent follow-up",
    solution: "Automated nurturing, appointment booking, reminders",
    outcome: "More consultations, higher treatment volume",
    img: "https://readdy.ai/api/search-image?query=luxury%20med%20spa%20wellness%20center%20clean%20white%20interior%20modern%20aesthetic%20clinic%20calm%20serene%20professional%20premium%20photography&width=800&height=500&seq=ind-medspa-003&orientation=landscape",
  },
  {
    icon: "ri-shield-line",
    title: "Insurance",
    subtitle: "Personal and commercial providers",
    painPoints: "Lead drop-off, manual quoting, slow response",
    solution: "Lead capture, nurturing sequences, pipeline automation",
    outcome: "Higher policy conversion rates",
    img: "https://readdy.ai/api/search-image?query=professional%20insurance%20office%20modern%20clean%20business%20environment%20neutral%20tones%20corporate%20minimal%20premium%20photography&width=800&height=500&seq=ind-insurance-004&orientation=landscape",
  },
  {
    icon: "ri-hospital-line",
    title: "Healthcare",
    subtitle: "Doctors, dentists, chiropractors, clinics",
    painPoints: "Scheduling inefficiencies, patient communication gaps",
    solution: "Automated intake, reminders, communication systems",
    outcome: "Improved patient flow and retention",
    img: "https://readdy.ai/api/search-image?query=modern%20medical%20clinic%20reception%20clean%20white%20interior%20professional%20healthcare%20facility%20minimal%20bright%20premium%20photography&width=800&height=500&seq=ind-health-005&orientation=landscape",
  },
];

export default function Industries() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? "bg-[#0a0a0a]" : "bg-white"}`}>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-40 pb-20 text-center px-6">
          <p className={`text-[11px] tracking-[0.15em] uppercase font-semibold mb-6 ${isDark ? "text-white/35" : "text-[#6e6e73]"}`}>Industries We Serve</p>
          <h1 className={`text-4xl md:text-6xl font-black max-w-3xl mx-auto leading-tight tracking-[-0.02em] mb-6 ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
            Built for Service-Based Industries
          </h1>
          <p className={`text-lg max-w-2xl mx-auto leading-relaxed font-light ${isDark ? "text-white/50" : "text-[#6e6e73]"}`}>
            We specialize in service-based industries where speed, response, and consistency directly impact revenue.
          </p>
        </section>

        {/* Industry Cards */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28 flex flex-col gap-5">
          {industries.map((ind, idx) => (
            <div
              key={ind.title}
              className={`flex flex-col ${idx % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"} rounded-2xl overflow-hidden border transition-colors duration-300 ${
                isDark ? "bg-[#111111] border-white/6" : "bg-[#f5f5f7] border-black/5"
              }`}
            >
              <div className="w-full lg:w-2/5 h-60 lg:h-auto flex-shrink-0">
                <img src={ind.img} alt={ind.title} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex-1 p-8 md:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${isDark ? "bg-white/8" : "bg-white"}`}>
                    <i className={`${ind.icon} text-xl ${isDark ? "text-white/75" : "text-[#1d1d1f]/75"}`}></i>
                  </div>
                  <div>
                    <h2 className={`font-black text-2xl ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{ind.title}</h2>
                    <p className={`text-sm ${isDark ? "text-white/35" : "text-[#6e6e73]"}`}>{ind.subtitle}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {[
                    { label: "Pain Points", value: ind.painPoints, accent: "border-red-400/50" },
                    { label: "Solution", value: ind.solution, accent: isDark ? "border-white/20" : "border-black/15" },
                    { label: "Outcome", value: ind.outcome, accent: "border-emerald-500/50" },
                  ].map((item) => (
                    <div key={item.label} className={`border-l-2 ${item.accent} pl-4`}>
                      <p className={`text-[10px] tracking-widest uppercase font-semibold mb-2 ${isDark ? "text-white/30" : "text-[#6e6e73]"}`}>{item.label}</p>
                      <p className={`text-sm leading-relaxed ${isDark ? "text-white/70" : "text-[#1d1d1f]/80"}`}>{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* CTA */}
        <section className={`py-24 text-center px-6 ${isDark ? "bg-[#111111]" : "bg-[#f5f5f7]"}`}>
          <h2 className={`text-3xl md:text-5xl font-black mb-6 tracking-[-0.02em] ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>Don&apos;t see your industry?</h2>
          <p className={`text-lg mb-10 max-w-xl mx-auto font-light ${isDark ? "text-white/50" : "text-[#6e6e73]"}`}>Our systems are adaptable. If you run a service business, we can build for you.</p>
          <Link to="/contact" className={`inline-block px-8 py-4 font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${isDark ? "bg-white text-[#0a0a0a] hover:bg-white/90" : "bg-[#1d1d1f] text-white hover:bg-[#1d1d1f]/85"}`}>
            Book Your AI Strategy Call
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}
